import React, { FC, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  FormProps,
  InputNumber,
  notification,
  Row,
  Select,
  UploadFile,
} from 'antd'
import Icon from '../../../components/Icon'
import RSelectUnits from '../../../components/RSelectUnits'
import RSaveOverlay from '../../../components/RSaveOverlay'
import { storageApi } from '../../../api'
import { GENERATION_TYPES, IPlant, IPlantForm } from '../../../types/plant'
import RDragger from '../../../components/RUpload'
import { normFile } from '../../../utils'
import { modelApi as modelApiInstance } from '../../../api/apiInstances'
import { IFilePostSuccesResponse } from '../../../types/file'
import { UploadChangeParam } from 'antd/lib/upload'
import moment from 'moment'

const generationTypes = Object.values(GENERATION_TYPES)
interface Props extends FormProps {
  initialValues?: { plants: IPlantForm[] }
  displayOverlayTriggerButton?: boolean
  allowMultiple?: boolean
}

const PlantForm: FC<Props> = ({
  initialValues,
  displayOverlayTriggerButton = false,
  allowMultiple = false,
  ...formProps
}) => {
  const [form] = Form.useForm()
  const [savedPlantGenerations, setSavedPlantGenerations] = useState<IPlant[]>([])
  const [lastFeedInTariffVal, setLastFeedInTariffVal] = useState<string>()
  // const [generatioinFileUrls, setGeneratioinFileUrls] = useState<string[]>([])
  const formInitialValues = initialValues || { plants: [{}] }

  async function fetchPlants() {
    const plantGenerations = await storageApi.fetchPlants()
    setSavedPlantGenerations(plantGenerations)
  }
  async function savePlantForm(saveName: string, plantIdx: number) {
    try {
      const values: IPlantForm = form.getFieldsValue().plants[plantIdx]
      const fileUrl = values.generationFile?.[0]?.url
      const feedInTariffDollarsEndDate = values.feedInTariffDollarsEndDate?.toISOString() || null

      await storageApi.createPlant({
        ...values,
        name: saveName,
        url: fileUrl || '',
        feedInTariffDollarsEndDate,
      })

      notification.success({ message: 'Plant Generation has been saved successfully' })
    } finally {
      fetchPlants()
    }
  }

  function updateDisableStateOfDatePicker(plantIdx: number, value: string) {
    const plants = [...form.getFieldValue('plants')]

    if (!value) {
      plants[plantIdx].feedInTariffDollarsEndDate = ''
      form.setFieldsValue({ plants })
    }
    setLastFeedInTariffVal(value)
  }

  function setFileDataIfResponseSuccessfull(
    plantIdx: number,
    fileResponse: UploadChangeParam<UploadFile<IFilePostSuccesResponse>>,
  ) {
    if (fileResponse.file.error || !fileResponse.fileList.length) return
    if (!fileResponse.file.response?.file_link) return

    const plants = [...form.getFieldValue('plants')]
    const uploadedFile = plants[plantIdx].generationFile[0]
    const downloadUrl = fileResponse.file.response?.file_link

    plants[plantIdx].generationFile = [{ ...uploadedFile, url: downloadUrl }]

    form.setFieldsValue({ plants })

    if (formProps.onValuesChange) {
      formProps.onValuesChange(form.getFieldsValue(), form.getFieldsValue())
    }
  }

  function setSelectedPlantGenerationData(
    savedPlantGenerationIdx: number,
    plantGenerationIdx: number,
  ) {
    const curPlants: IPlantForm[] = [...form.getFieldValue('plants')]
    const {
      generationType,
      installedPowerKw,
      licensedPowerKw,
      feedInTariffDollars,
      feedInTariffDollarsEndDate,
      url,
    } = savedPlantGenerations[savedPlantGenerationIdx]
    curPlants[plantGenerationIdx] = {
      generationType,
      installedPowerKw,
      licensedPowerKw,
      feedInTariffDollars,
      feedInTariffDollarsEndDate: moment(feedInTariffDollarsEndDate),
      generationFile: [{ uid: savedPlantGenerationIdx.toString(), name: url, url: url }],
    }

    form.setFieldsValue({ plants: curPlants })
    formProps.onValuesChange?.(form.getFieldsValue(), form.getFieldsValue())
  }

  useEffect(() => {
    fetchPlants()
  }, [])

  return (
    <>
      <Form form={form} layout='vertical' initialValues={formInitialValues} {...formProps}>
        <Form.List name='plants'>
          {(fields, { add, remove }) => (
            <>
              {allowMultiple && (
                <Button className='mb-24' type='primary' block onClick={add}>
                  + Add Plant
                </Button>
              )}

              {fields.map(({ key, name, ...restField }, idx) => (
                <RSaveOverlay
                  key={key}
                  displayDelete={allowMultiple}
                  displayOverlayTriggerButton={displayOverlayTriggerButton}
                  loadDataOptions={savedPlantGenerations.map((plantGeneration, idx) => ({
                    label: plantGeneration.name,
                    value: idx,
                  }))}
                  onLoadData={(val) => setSelectedPlantGenerationData(val, idx)}
                  onSave={(saveName) => savePlantForm(saveName, idx)}
                  onDelete={() => remove(name)}
                >
                  <Card className={`ticket-primary ${fields.length !== idx + 1 ? 'mb-24' : ''}`}>
                    <Row gutter={40}>
                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          label='Generation Type'
                          name={[name, 'generationType']}
                        >
                          <Select>
                            {generationTypes.map(({ label, value, icon }) => (
                              <Select.Option key={value}>
                                <Icon icon={icon}></Icon>
                                {label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label='Feed-in-Tariff'
                          name={[name, 'feedInTariffDollars']}
                        >
                          <InputNumber
                            className='w-100'
                            addonAfter={'$'}
                            onInput={(val) => updateDisableStateOfDatePicker(idx, val)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label='Feed-in-Tariff End Date'
                          name={[name, 'feedInTariffDollarsEndDate']}
                        >
                          <DatePicker
                            className='w-100'
                            disabled={!form.getFieldValue('plants')[idx].feedInTariffDollars}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label='Installed Power'
                          name={[name, 'installedPowerKw']}
                        >
                          <InputNumber className='w-100' addonAfter={<RSelectUnits />} />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          label='Licensed Power'
                          name={[name, 'licensedPowerKw']}
                        >
                          <InputNumber className='w-100' addonAfter={<RSelectUnits />} />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          label='Generation'
                          name={[name, 'generationFile']}
                          valuePropName='fileList'
                          getValueFromEvent={normFile}
                        >
                          <RDragger
                            action={`${modelApiInstance.baseURL}/file/generationproject`}
                            onChange={(info) => {
                              setFileDataIfResponseSuccessfull(idx, info)
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </RSaveOverlay>
              ))}
            </>
          )}
        </Form.List>
      </Form>
    </>
  )
}

export default PlantForm
